import React from 'react';
import { X, Send, Download } from 'lucide-react';
import { formatAmount, getCurrencySymbol } from './invoiceHelpers';

import SelectMenu from "../crm/SelectMenu";
// Contact Change Modal
export const ContactChangeModal = ({
  isOpen,
  onClose,
  allClients,
  selectedContactId,
  setSelectedContactId,
  onConfirm
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-2xl w-full m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Change Contact</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-3">
            {allClients.map((client) => (
              <div
                key={client.id}
                onClick={() => setSelectedContactId(client.id)}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedContactId === client.id
                    ? "border-blue-500 bg-blue-500 bg-opacity-10"
                    : "border-gray-700 hover:border-gray-600 bg-[#0f0f23]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">
                      {client.salutation
                        ? `${client.salutation} ${client.name}`
                        : client.name}
                    </p>
                    {client.company && (
                      <p className="text-sm text-gray-400">{client.company}</p>
                    )}
                    {client.email && (
                      <p className="text-sm text-gray-400">{client.email}</p>
                    )}
                    {client.contact && (
                      <p className="text-sm text-gray-400">{client.contact}</p>
                    )}
                  </div>
                  {selectedContactId === client.id && (
                    <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors"
          >
            Update Contact
          </button>
        </div>
      </div>
    </div>
  );
};

// Due Date Edit Modal
export const DueDateModal = ({
  isOpen,
  onClose,
  dueDate,
  setDueDate,
  onConfirm
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-md w-full m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Edit Due Date</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <label className="block text-gray-400 text-sm mb-2">
            Due Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors [color-scheme:dark]"
          />
        </div>

        <div className="flex gap-4 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors"
          >
            Update Due Date
          </button>
        </div>
      </div>
    </div>
  );
};

// Capture Payment Modal
export const CapturePaymentModal = ({
  isOpen,
  onClose,
  invoice,
  paymentData,
  setPaymentData,
  editingPayment,
  setEditingPayment,
  onSubmit
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    setEditingPayment(null);
    setPaymentData({
      amount: "",
      date: new Date().toISOString().split("T")[0],
      mode: "Cash",
      reference: "",
      notes: "",
      tds: ""
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[60] pt-20 overflow-y-auto">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-3xl w-full mx-4 my-6">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">
            {editingPayment ? "Edit Payment" : "Capture Payment"}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Payment Amount */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Payment Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {getCurrencySymbol(invoice.currency)}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={paymentData.amount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
                    setPaymentData({ ...paymentData, amount: val });
                  }}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Due Amount: {formatAmount(invoice.dueAmount || invoice.totalAmount, invoice.currency)}
              </p>
            </div>

            {/* TDS Amount */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                TDS Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {getCurrencySymbol(invoice.currency)}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={paymentData.tds}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
                    setPaymentData({ ...paymentData, tds: val });
                  }}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Tax Deducted at Source
              </p>
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={paymentData.date}
                onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors [color-scheme:dark]"
              />
            </div>

            {/* Payment Mode */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Payment Mode <span className="text-red-500">*</span>
              </label>
              <SelectMenu
                value={paymentData.mode}
                onChange={(e) => setPaymentData({ ...paymentData, mode: e.target.value })}
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
              >
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </SelectMenu>
            </div>

            {/* Reference Number */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Reference/Transaction Number
              </label>
              <input
                type="text"
                value={paymentData.reference}
                onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                placeholder="Enter reference or transaction number"
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
              />
            </div>

            {/* Notes - Full Width */}
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-2">
                Notes
              </label>
              <textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                placeholder="Add any additional notes"
                rows="2"
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors resize-none"
              ></textarea>
            </div>
          </div>

          {/* Buttons - Centered and Smaller */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors text-sm"
            >
              {editingPayment ? "Update Payment" : "Capture Payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Credit Note Modal
export const CreditNoteModal = ({
  isOpen,
  onClose,
  invoice,
  creditNoteData,
  setCreditNoteData,
  editingCreditNote,
  setEditingCreditNote,
  onSubmit
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    setEditingCreditNote(null);
    setCreditNoteData({
      amount: "",
      date: new Date().toISOString().split("T")[0],
      reason: "",
      reference: "",
      notes: ""
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[60] pt-20 overflow-y-auto">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-2xl w-full mx-4 my-6">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">
            {editingCreditNote ? "Edit Credit Note" : "Add Credit Note"}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Credit Note Amount */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Credit Note Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {getCurrencySymbol(invoice.currency)}
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={creditNoteData.amount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
                    setCreditNoteData({ ...creditNoteData, amount: val });
                  }}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Due Amount: {formatAmount(invoice.dueAmount || invoice.totalAmount, invoice.currency)}
              </p>
            </div>

            {/* Credit Note Date */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Credit Note Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={creditNoteData.date}
                onChange={(e) => setCreditNoteData({ ...creditNoteData, date: e.target.value })}
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors [color-scheme:dark]"
              />
            </div>

            {/* Reason - Full Width */}
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-2">
                Reason for Credit Note <span className="text-red-500">*</span>
              </label>
              <SelectMenu
                value={creditNoteData.reason}
                onChange={(e) => setCreditNoteData({ ...creditNoteData, reason: e.target.value })}
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
              >
                <option value="">Select reason</option>
                <option value="Billing Error">Billing Error</option>
                <option value="Service Issue">Service Issue</option>
                <option value="Discount">Discount</option>
                <option value="Partial Refund">Partial Refund</option>
                <option value="Adjustment">Adjustment</option>
                <option value="Goodwill">Goodwill</option>
                <option value="Other">Other</option>
              </SelectMenu>
            </div>

            {/* Reference Number */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Reference Number
              </label>
              <input
                type="text"
                value={creditNoteData.reference}
                onChange={(e) => setCreditNoteData({ ...creditNoteData, reference: e.target.value })}
                placeholder="Enter reference number"
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors"
              />
            </div>

            {/* Notes - Full Width */}
            <div className="md:col-span-2">
              <label className="block text-gray-400 text-sm mb-2">
                Notes
              </label>
              <textarea
                value={creditNoteData.notes}
                onChange={(e) => setCreditNoteData({ ...creditNoteData, notes: e.target.value })}
                placeholder="Add any additional notes or details"
                rows="3"
                className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#C99F4A] transition-colors resize-none"
              ></textarea>
            </div>
          </div>

          {/* Buttons - Centered and Smaller */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={handleClose}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors text-sm"
            >
              {editingCreditNote ? "Update Credit Note" : "Add Credit Note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Send Invoice Confirmation Modal
export const SendInvoiceModal = ({
  isOpen,
  onClose,
  invoice,
  clientDetails,
  sendingEmail,
  onSend
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-md w-full m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Send Invoice to Client</h3>
          <button
            onClick={onClose}
            disabled={sendingEmail}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-400 mb-4">
            Are you sure you want to send this invoice to the client?
          </p>

          <div className="bg-[#0f0f23] border border-gray-700 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-400 mb-2">Client Email:</p>
            <p className="text-white font-medium">{clientDetails?.email}</p>
          </div>

          <div className="bg-[#0f0f23] border border-gray-700 rounded-lg p-4">
            <p className="text-sm text-gray-400 mb-2">Invoice Number:</p>
            <p className="text-white font-medium">{invoice.invoiceNumber}</p>
            <p className="text-sm text-gray-400 mt-2 mb-1">Amount:</p>
            <p className="text-white font-medium">{formatAmount(invoice.totalAmount, invoice.currency)}</p>
          </div>
        </div>

        <div className="flex gap-4 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            disabled={sendingEmail}
            className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            No, Cancel
          </button>
          <button
            onClick={onSend}
            disabled={sendingEmail}
            className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sendingEmail ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sending...
              </>
            ) : (
              <>
                <Send size={16} />
                Yes, Send Invoice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// View PDF Modal
export const ViewPDFModal = ({
  isOpen,
  onClose,
  invoice,
  pdfDataUrl,
  setPdfDataUrl,
  onDownload
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    setPdfDataUrl("");
  };

  const handleDownload = () => {
    onDownload();
    handleClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-6xl w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-semibold text-white">Invoice Preview - {invoice.invoiceNumber}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 p-6 overflow-hidden">
          {pdfDataUrl && (
            <iframe
              src={pdfDataUrl}
              className="w-full h-full rounded-lg border border-gray-700"
              title="Invoice PDF Preview"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleDownload}
            className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};
